"use client";

import { Box, Spacer, Stack, Text, VStack } from "@chakra-ui/react";
import BrandedPrototypeSelector from "@/shared/ui/controls/BrandedPrototypeSelector";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import { useAuth, useUserChoices } from "@/state/globalStore";
import {
  MindstringPrototype,
  type SelectBySituationScope,
  type SelectedThought,
  type SelectedThoughtFree,
} from "@/generated/fastapi";
import ReflectQuestionnaire from "./ReflectQuestionnaire";
import ReflectQuestionnaireControls from "./ReflectQuestionnaireControls";
import {
  getVisibleMindstringPrototypeCodes,
  mindstringPrototypesSlang,
  normalizeVisibleMindstringPrototype,
} from "@/shared/config/backend";
import { THOUGHT_QUERY_DEFAULTS } from "@/features/thoughts";
import { useEffect, useMemo, useState } from "react";

type PrototypeThoughtCounts = Record<MindstringPrototype, number>;

function countThoughtsByPrototype({
  selectedThoughts,
  selectedThoughtsFree,
  situationId,
  visiblePrototypes,
}: {
  selectedThoughts: SelectedThought[];
  selectedThoughtsFree: SelectedThoughtFree[];
  situationId?: string | null;
  visiblePrototypes: readonly MindstringPrototype[];
}): PrototypeThoughtCounts {
  const visiblePrototypeSet = new Set<MindstringPrototype>(visiblePrototypes);
  const thoughtIdsByPrototype = new Map<MindstringPrototype, Set<string>>();

  const ensurePrototypeSet = (prototype: MindstringPrototype) => {
    const existingSet = thoughtIdsByPrototype.get(prototype);
    if (existingSet) {
      return existingSet;
    }

    const nextSet = new Set<string>();
    thoughtIdsByPrototype.set(prototype, nextSet);
    return nextSet;
  };

  selectedThoughts.forEach((selection) => {
    if (!visiblePrototypeSet.has(selection.prototype)) {
      return;
    }

    if (situationId && selection.situation_id !== situationId) {
      return;
    }

    const prototypeThoughtIds = ensurePrototypeSet(selection.prototype);
    selection.thoughts_ids.forEach((thoughtId) =>
      prototypeThoughtIds.add(thoughtId),
    );
  });

  if (!situationId) {
    selectedThoughtsFree.forEach((selection) => {
      if (!visiblePrototypeSet.has(selection.prototype)) {
        return;
      }

      const prototypeThoughtIds = ensurePrototypeSet(selection.prototype);
      selection.thoughts_ids.forEach((thoughtId) =>
        prototypeThoughtIds.add(thoughtId),
      );
    });
  }

  return Object.fromEntries(
    visiblePrototypes.map((prototype) => [
      prototype,
      thoughtIdsByPrototype.get(prototype)?.size ?? 0,
    ]),
  ) as PrototypeThoughtCounts;
}

function formatThoughtCount(count: number) {
  return `${count} ${count === 1 ? "selected thought" : "selected thoughts"}`;
}

function getPreferredActivePrototype({
  normalizedPrototype,
  selectedPrototypes,
}: {
  normalizedPrototype: MindstringPrototype;
  selectedPrototypes: MindstringPrototype[];
}) {
  if (selectedPrototypes.length === 1) {
    return selectedPrototypes[0];
  }

  return normalizedPrototype;
}

export interface ReflectThoughtsProps {
  situationId?: string | null;
  situationScope?: SelectBySituationScope;
  prototype: MindstringPrototype;
  limit?: number;
}

export default function ReflectThoughts({
  situationId,
  situationScope = THOUGHT_QUERY_DEFAULTS.situationScope,
  prototype,
  limit,
}: ReflectThoughtsProps) {
  const { isUserAdmin } = useAuth();
  const normalizedPrototype = normalizeVisibleMindstringPrototype(
    prototype,
    isUserAdmin,
  );
  const {
    selectedThoughts,
    selectedThoughtsFree,
    questionnaireViewModes,
  } = useUserChoices();

  const visiblePrototypes = useMemo(
    () => getVisibleMindstringPrototypeCodes(isUserAdmin),
    [isUserAdmin],
  );
  const selectedThoughtCountsByPrototype = useMemo(
    () =>
      countThoughtsByPrototype({
        selectedThoughts,
        selectedThoughtsFree,
        situationId,
        visiblePrototypes,
      }),
    [selectedThoughts, selectedThoughtsFree, situationId, visiblePrototypes],
  );
  const selectedPrototypes = useMemo(
    () =>
      visiblePrototypes.filter(
        (currentPrototype) =>
          (selectedThoughtCountsByPrototype[currentPrototype] ?? 0) > 0,
      ),
    [selectedThoughtCountsByPrototype, visiblePrototypes],
  );
  const preferredActivePrototype = getPreferredActivePrototype({
    normalizedPrototype,
    selectedPrototypes,
  });
  const [activePrototype, setActivePrototype] = useState<MindstringPrototype>(
    preferredActivePrototype,
  );

  useEffect(() => {
    setActivePrototype(preferredActivePrototype);
  }, [preferredActivePrototype]);

  const activeSelectedThoughtCount =
    selectedThoughtCountsByPrototype[activePrototype] ?? 0;
  const otherSelectedThoughtCount = visiblePrototypes.reduce(
    (count, currentPrototype) =>
      currentPrototype === activePrototype
        ? count
        : count + (selectedThoughtCountsByPrototype[currentPrototype] ?? 0),
    0,
  );
  const prototypeItemsWithCounts = useMemo(
    () =>
      visiblePrototypes
        .filter(
          (currentPrototype) =>
            currentPrototype === activePrototype ||
            (selectedThoughtCountsByPrototype[currentPrototype] ?? 0) > 0,
        )
        .map((currentPrototype) => {
          const metadata = mindstringPrototypesSlang[currentPrototype];

          return {
            label: metadata.label,
            value: currentPrototype,
            description: metadata.description,
            icon: metadata.icon,
            count: selectedThoughtCountsByPrototype[currentPrototype] ?? 0,
          };
        }),
    [activePrototype, selectedThoughtCountsByPrototype, visiblePrototypes],
  );
  const thoughtScopeLabel =
    activeSelectedThoughtCount > 0
      ? formatThoughtCount(activeSelectedThoughtCount)
      : limit
        ? `Up to ${limit} thoughts`
        : "Selected thoughts";
  const otherPerspectivesLabel =
    otherSelectedThoughtCount > 0
      ? `${formatThoughtCount(otherSelectedThoughtCount)} in other perspectives`
      : null;
  const activePerspectiveLabel = mindstringPrototypesSlang[activePrototype].label;

  return (
    <Box>
      <VStack align="stretch">
        <GenericDrawerInfo
          description="Reflect selected thoughts so the profile understands their strength and shape. The questionnaire below is prepared for the active perspective."
          items={[
            {
              label: thoughtScopeLabel,
              colorPalette: "blue",
            },
            ...(otherPerspectivesLabel
              ? [
                  {
                    label: otherPerspectivesLabel,
                    colorPalette: "purple" as const,
                  },
                ]
              : []),
          ]}
        >
          <Stack gap="2" align="stretch">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="moveon.text.muted"
            >
              Active perspective
            </Text>
            <BrandedPrototypeSelector
              activePrototype={activePrototype}
              options={prototypeItemsWithCounts}
              onChange={setActivePrototype}
              ariaLabel="Choose reflection perspective"
              disabled={prototypeItemsWithCounts.length <= 1}
              showCounts
              showDescriptions
              triggerDescription
              variant="compact"
              countNoun="thought"
              countNounPlural="thoughts"
            />
            <Text fontSize="xs" color="moveon.text.muted">
              {otherSelectedThoughtCount > 0
                ? "Switch perspective to reflect the other selected thoughts."
                : `Questions are prepared for ${activePerspectiveLabel}.`}
            </Text>
          </Stack>
        </GenericDrawerInfo>

        <ReflectQuestionnaireControls />
      </VStack>

      <Spacer h="6" />

      <ReflectQuestionnaire
        situationId={situationId}
        situationScope={situationScope}
        prototype={activePrototype}
        limit={limit}
        viewMode={questionnaireViewModes[0]}
      />
    </Box>
  );
}
